
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Vehicle, VehicleFormData } from '../types';

const fetchVehicles = async (): Promise<Vehicle[]> => {
  console.log('🚗 Fetching vehicles...');
  
  const { data, error } = await supabase
    .from('vehicles')
    .select(`
      id, title, description, price, category, date_added,
      year, mileage, show_price
    `)
    .eq('is_available', true)
    .order('date_added', { ascending: false });

  if (error) {
    console.error('❌ Error fetching vehicles:', error);
    throw error;
  }
  
  console.log('✅ Vehicles fetched successfully:', data?.length || 0, 'vehicles');

  return data.map(vehicle => ({
    id: vehicle.id,
    title: vehicle.title,
    description: vehicle.description,
    price: Number(vehicle.price),
    images: [], // Images loaded separately to improve initial load performance
    category: vehicle.category as Vehicle['category'],
    dateAdded: vehicle.date_added,
    isAvailable: true as const,
    
    // Basic specs for display
    year: vehicle.year,
    mileage: vehicle.mileage,
    
    // Visibility controls
    showPrice: vehicle.show_price,

    // Default values for missing fields (required by Vehicle type)
    videos: [],
    fuelType: undefined,
    transmission: undefined,
    enginePower: undefined,
    engineDisplacement: undefined,
    axleConfiguration: undefined,
    maxWeight: undefined,
    payloadCapacity: undefined,
    drivingLicenseRequired: undefined,
    ptav: undefined,
    ptra: undefined,
    wheelbase: undefined,
    critAir: undefined,
    serialNumber: undefined,
    euroStandard: undefined,
    bodyType: undefined,
    suspensionType: undefined,
    powerSteering: false,
    drivingPosition: undefined,
    priceHT: undefined,
    priceTTC: undefined,
    hasAirConditioning: undefined,
    hasAbs: undefined,
    hasEsp: undefined,
    hasCruiseControl: undefined,
    hasNavigation: undefined,
    hasReversingCamera: undefined,
    hasParkingSensors: undefined,
    hasCentralLocking: undefined,
    hasElectricWindows: undefined,
    hasHydraulicLift: undefined,
    additionalEquipment: undefined,
    frontTireSize: undefined,
    rearTireSize: undefined,
    tireWearPercentage: undefined,
    lastServiceDate: undefined,
    nextServiceDue: undefined,
    technicalInspectionDate: undefined,
    technicalInspectionValidUntil: undefined,
    previousOwners: undefined,
    accidentHistory: undefined,
    accidentDetails: undefined,
    generalCondition: 'bon' as Vehicle['generalCondition'],
    interiorCondition: 'bon' as Vehicle['interiorCondition'],
    exteriorCondition: 'bon' as Vehicle['exteriorCondition'],
    usageType: 'professionnel' as Vehicle['usageType'],
    parkingLocation: undefined,
    availabilityDate: undefined,
    warrantyRemaining: undefined,
    financingAvailable: undefined,
    tradeInAccepted: undefined,
    notes: undefined,
    showMileage: true,
    showTechnicalDetails: true,
    showEquipment: true,
    showMaintenance: false,
    showHistory: false,
    showWarranty: true,
    showLocation: true,
    showContactInfo: true
  }));
};

const deleteVehicle = async (id: string): Promise<void> => {
  console.log('Deleting vehicle:', id);
  const { error } = await supabase
    .from('vehicles')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting vehicle:', error);
    throw error;
  }
  
  console.log('Vehicle deleted successfully:', id);
};

const createVehicle = async (vehicleData: VehicleFormData): Promise<Vehicle> => {
  console.log('Creating vehicle:', vehicleData);
  
  const insertData = {
    title: vehicleData.title,
    description: vehicleData.description,
    price: vehicleData.price,
    images: vehicleData.images,
    videos: vehicleData.videos || [],
    category: vehicleData.category,
    is_available: true,
    
    // Technical specifications
    year: vehicleData.year === 'avant-1990' ? null : (typeof vehicleData.year === 'number' ? vehicleData.year : null),
    mileage: vehicleData.mileage,
    fuel_type: vehicleData.fuelType,
    transmission: vehicleData.transmission,
    engine_power: vehicleData.enginePower,
    engine_displacement: vehicleData.engineDisplacement,
    axle_configuration: vehicleData.axleConfiguration,
    max_weight: vehicleData.maxWeight,
    payload_capacity: vehicleData.payloadCapacity,
    driving_license_required: vehicleData.drivingLicenseRequired,
    
    // Equipment and options
    has_air_conditioning: vehicleData.hasAirConditioning,
    has_abs: vehicleData.hasAbs,
    has_esp: vehicleData.hasEsp,
    has_cruise_control: vehicleData.hasCruiseControl,
    has_navigation: vehicleData.hasNavigation,
    has_reversing_camera: vehicleData.hasReversingCamera,
    has_parking_sensors: vehicleData.hasParkingSensors,
    has_central_locking: vehicleData.hasCentralLocking,
    has_electric_windows: vehicleData.hasElectricWindows,
    has_hydraulic_lift: vehicleData.hasHydraulicLift,
    additional_equipment: vehicleData.additionalEquipment,
    
    // NOTE: Nouveaux champs temporairement commentés jusqu'à la migration de la DB
    // ptav: vehicleData.ptav,
    // ptra: vehicleData.ptra,
    // wheelbase: vehicleData.wheelbase,
    // crit_air: vehicleData.critAir,
    // serial_number: vehicleData.serialNumber,
    // euro_standard: vehicleData.euroStandard,
    // body_type: vehicleData.bodyType,
    // suspension_type: vehicleData.suspensionType,
    // power_steering: vehicleData.powerSteering,
    // driving_position: vehicleData.drivingPosition,
    // price_ht: vehicleData.priceHT,
    // price_ttc: vehicleData.priceTTC,
    
    // Tires and maintenance
    front_tire_size: vehicleData.frontTireSize,
    rear_tire_size: vehicleData.rearTireSize,
    // tire_wear_percentage: vehicleData.tireWearPercentage, // Temporairement commenté
    last_service_date: vehicleData.lastServiceDate,
    next_service_due: vehicleData.nextServiceDue,
    technical_inspection_date: vehicleData.technicalInspectionDate,
    technical_inspection_valid_until: vehicleData.technicalInspectionValidUntil,
    
    // Vehicle history and condition
    previous_owners: vehicleData.previousOwners,
    accident_history: vehicleData.accidentHistory,
    accident_details: vehicleData.accidentDetails,
    general_condition: vehicleData.generalCondition,
    interior_condition: vehicleData.interiorCondition,
    exterior_condition: vehicleData.exteriorCondition,
    
    // Additional information
    usage_type: vehicleData.usageType,
    parking_location: vehicleData.parkingLocation,
    availability_date: vehicleData.availabilityDate,
    warranty_remaining: vehicleData.warrantyRemaining,
    financing_available: vehicleData.financingAvailable,
    trade_in_accepted: vehicleData.tradeInAccepted,
    notes: vehicleData.notes,
    
    // Visibility controls
    show_price: vehicleData.showPrice,
    show_mileage: vehicleData.showMileage,
    show_technical_details: vehicleData.showTechnicalDetails,
    show_equipment: vehicleData.showEquipment,
    show_maintenance: vehicleData.showMaintenance,
    show_history: vehicleData.showHistory,
    show_warranty: vehicleData.showWarranty,
    show_location: vehicleData.showLocation,
    show_contact_info: vehicleData.showContactInfo
  };
  
  console.log('Insert data:', insertData);
  
  const { data, error } = await supabase
    .from('vehicles')
    .insert([insertData])
    .select()
    .single();

  if (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }

  console.log('Vehicle created successfully:', data);

  // Transform database format to application format
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    price: Number(data.price),
    images: data.images || [],
    videos: data.videos || [],
    category: data.category as Vehicle['category'],
    dateAdded: data.date_added,
    isAvailable: true as const,
    
    // Technical specifications
    year: data.year || undefined,
    mileage: data.mileage,
    fuelType: data.fuel_type as Vehicle['fuelType'],
    transmission: data.transmission as Vehicle['transmission'],
    enginePower: data.engine_power,
    engineDisplacement: data.engine_displacement,
    axleConfiguration: data.axle_configuration,
    maxWeight: data.max_weight,
    payloadCapacity: data.payload_capacity,
    drivingLicenseRequired: data.driving_license_required,
    
    // Nouvelles spécifications techniques - Avec valeurs par défaut temporaires
    ptav: (data as any).ptav || undefined,
    ptra: (data as any).ptra || undefined,
    wheelbase: (data as any).wheelbase || undefined,
    critAir: (data as any).crit_air || undefined,
    serialNumber: (data as any).serial_number || undefined,
    euroStandard: (data as any).euro_standard || undefined,
    bodyType: (data as any).body_type || undefined,
    suspensionType: (data as any).suspension_type || undefined,
    powerSteering: (data as any).power_steering || false,
    drivingPosition: (data as any).driving_position || undefined,
    priceHT: (data as any).price_ht || undefined,
    priceTTC: (data as any).price_ttc || undefined,
    
    // Equipment and options
    hasAirConditioning: data.has_air_conditioning,
    hasAbs: data.has_abs,
    hasEsp: data.has_esp,
    hasCruiseControl: data.has_cruise_control,
    hasNavigation: data.has_navigation,
    hasReversingCamera: data.has_reversing_camera,
    hasParkingSensors: data.has_parking_sensors,
    hasCentralLocking: data.has_central_locking,
    hasElectricWindows: data.has_electric_windows,
    hasHydraulicLift: data.has_hydraulic_lift,
    additionalEquipment: data.additional_equipment,
    
    // Tires and maintenance
    frontTireSize: data.front_tire_size,
    rearTireSize: data.rear_tire_size,
    tireWearPercentage: (data as any).tire_wear_percentage || undefined,
    lastServiceDate: data.last_service_date,
    nextServiceDue: data.next_service_due,
    technicalInspectionDate: data.technical_inspection_date,
    technicalInspectionValidUntil: data.technical_inspection_valid_until,
    
    // Vehicle history and condition
    previousOwners: data.previous_owners,
    accidentHistory: data.accident_history,
    accidentDetails: data.accident_details,
    generalCondition: data.general_condition as Vehicle['generalCondition'],
    interiorCondition: data.interior_condition as Vehicle['interiorCondition'],
    exteriorCondition: data.exterior_condition as Vehicle['exteriorCondition'],
    
    // Additional information
    usageType: data.usage_type as Vehicle['usageType'],
    parkingLocation: data.parking_location,
    availabilityDate: data.availability_date,
    warrantyRemaining: data.warranty_remaining,
    financingAvailable: data.financing_available,
    tradeInAccepted: data.trade_in_accepted,
    notes: data.notes,
    
    // Visibility controls
    showPrice: data.show_price,
    showMileage: data.show_mileage,
    showTechnicalDetails: data.show_technical_details,
    showEquipment: data.show_equipment,
    showMaintenance: data.show_maintenance,
    showHistory: data.show_history,
    showWarranty: data.show_warranty,
    showLocation: data.show_location,
    showContactInfo: data.show_contact_info
  };
};

const updateVehicle = async (id: string, vehicleData: VehicleFormData): Promise<Vehicle> => {
  console.log('Updating vehicle:', id, vehicleData);
  
  const { data, error } = await supabase
    .from('vehicles')
    .update({
      title: vehicleData.title,
      description: vehicleData.description,
      price: vehicleData.price,
      images: vehicleData.images,
      videos: vehicleData.videos || [],
      category: vehicleData.category,
      
      // Technical specifications
      year: vehicleData.year === 'avant-1990' ? null : (typeof vehicleData.year === 'number' ? vehicleData.year : null),
      mileage: vehicleData.mileage,
      fuel_type: vehicleData.fuelType,
      transmission: vehicleData.transmission,
      engine_power: vehicleData.enginePower,
      engine_displacement: vehicleData.engineDisplacement,
      axle_configuration: vehicleData.axleConfiguration,
      max_weight: vehicleData.maxWeight,
      payload_capacity: vehicleData.payloadCapacity,
      driving_license_required: vehicleData.drivingLicenseRequired,
      
      // NOTE: Nouveaux champs temporairement commentés jusqu'à la migration de la DB
      // ptav: vehicleData.ptav,
      // ptra: vehicleData.ptra,
      // wheelbase: vehicleData.wheelbase,
      // crit_air: vehicleData.critAir,
      // serial_number: vehicleData.serialNumber,
      // euro_standard: vehicleData.euroStandard,
      // body_type: vehicleData.bodyType,
      // suspension_type: vehicleData.suspensionType,
      // power_steering: vehicleData.powerSteering,
      // driving_position: vehicleData.drivingPosition,
      // price_ht: vehicleData.priceHT,
      // price_ttc: vehicleData.priceTTC,
      
      // Equipment and options
      has_air_conditioning: vehicleData.hasAirConditioning,
      has_abs: vehicleData.hasAbs,
      has_esp: vehicleData.hasEsp,
      has_cruise_control: vehicleData.hasCruiseControl,
      has_navigation: vehicleData.hasNavigation,
      has_reversing_camera: vehicleData.hasReversingCamera,
      has_parking_sensors: vehicleData.hasParkingSensors,
      has_central_locking: vehicleData.hasCentralLocking,
      has_electric_windows: vehicleData.hasElectricWindows,
      has_hydraulic_lift: vehicleData.hasHydraulicLift,
      additional_equipment: vehicleData.additionalEquipment,
      
      // Tires and maintenance
      front_tire_size: vehicleData.frontTireSize,
      rear_tire_size: vehicleData.rearTireSize,
      // tire_wear_percentage: vehicleData.tireWearPercentage, // Temporairement commenté
      last_service_date: vehicleData.lastServiceDate,
      next_service_due: vehicleData.nextServiceDue,
      technical_inspection_date: vehicleData.technicalInspectionDate,
      technical_inspection_valid_until: vehicleData.technicalInspectionValidUntil,
      
      // Vehicle history and condition
      previous_owners: vehicleData.previousOwners,
      accident_history: vehicleData.accidentHistory,
      accident_details: vehicleData.accidentDetails,
      general_condition: vehicleData.generalCondition,
      interior_condition: vehicleData.interiorCondition,
      exterior_condition: vehicleData.exteriorCondition,
      
      // Additional information
      usage_type: vehicleData.usageType,
      parking_location: vehicleData.parkingLocation,
      availability_date: vehicleData.availabilityDate,
      warranty_remaining: vehicleData.warrantyRemaining,
      financing_available: vehicleData.financingAvailable,
      trade_in_accepted: vehicleData.tradeInAccepted,
      notes: vehicleData.notes,
      
      // Visibility controls
      show_price: vehicleData.showPrice,
      show_mileage: vehicleData.showMileage,
      show_technical_details: vehicleData.showTechnicalDetails,
      show_equipment: vehicleData.showEquipment,
      show_maintenance: vehicleData.showMaintenance,
      show_history: vehicleData.showHistory,
      show_warranty: vehicleData.showWarranty,
      show_location: vehicleData.showLocation,
      show_contact_info: vehicleData.showContactInfo
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating vehicle:', error);
    throw error;
  }

  console.log('Vehicle updated successfully:', data);

  // Transform database format to application format
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    price: Number(data.price),
    images: data.images || [],
    videos: data.videos || [],
    category: data.category as Vehicle['category'],
    dateAdded: data.date_added,
    isAvailable: true as const,
    
    // Technical specifications
    year: data.year || undefined,
    mileage: data.mileage,
    fuelType: data.fuel_type as Vehicle['fuelType'],
    transmission: data.transmission as Vehicle['transmission'],
    enginePower: data.engine_power,
    engineDisplacement: data.engine_displacement,
    axleConfiguration: data.axle_configuration,
    maxWeight: data.max_weight,
    payloadCapacity: data.payload_capacity,
    drivingLicenseRequired: data.driving_license_required,
    
    // Nouvelles spécifications techniques - Avec valeurs par défaut temporaires
    ptav: (data as any).ptav || undefined,
    ptra: (data as any).ptra || undefined,
    wheelbase: (data as any).wheelbase || undefined,
    critAir: (data as any).crit_air || undefined,
    serialNumber: (data as any).serial_number || undefined,
    euroStandard: (data as any).euro_standard || undefined,
    bodyType: (data as any).body_type || undefined,
    suspensionType: (data as any).suspension_type || undefined,
    powerSteering: (data as any).power_steering || false,
    drivingPosition: (data as any).driving_position || undefined,
    priceHT: (data as any).price_ht || undefined,
    priceTTC: (data as any).price_ttc || undefined,
    
    // Equipment and options
    hasAirConditioning: data.has_air_conditioning,
    hasAbs: data.has_abs,
    hasEsp: data.has_esp,
    hasCruiseControl: data.has_cruise_control,
    hasNavigation: data.has_navigation,
    hasReversingCamera: data.has_reversing_camera,
    hasParkingSensors: data.has_parking_sensors,
    hasCentralLocking: data.has_central_locking,
    hasElectricWindows: data.has_electric_windows,
    hasHydraulicLift: data.has_hydraulic_lift,
    additionalEquipment: data.additional_equipment,
    
    // Tires and maintenance
    frontTireSize: data.front_tire_size,
    rearTireSize: data.rear_tire_size,
    tireWearPercentage: (data as any).tire_wear_percentage || undefined,
    lastServiceDate: data.last_service_date,
    nextServiceDue: data.next_service_due,
    technicalInspectionDate: data.technical_inspection_date,
    technicalInspectionValidUntil: data.technical_inspection_valid_until,
    
    // Vehicle history and condition
    previousOwners: data.previous_owners,
    accidentHistory: data.accident_history,
    accidentDetails: data.accident_details,
    generalCondition: data.general_condition as Vehicle['generalCondition'],
    interiorCondition: data.interior_condition as Vehicle['interiorCondition'],
    exteriorCondition: data.exterior_condition as Vehicle['exteriorCondition'],
    
    // Additional information
    usageType: data.usage_type as Vehicle['usageType'],
    parkingLocation: data.parking_location,
    availabilityDate: data.availability_date,
    warrantyRemaining: data.warranty_remaining,
    financingAvailable: data.financing_available,
    tradeInAccepted: data.trade_in_accepted,
    notes: data.notes,
    
    // Visibility controls
    showPrice: data.show_price,
    showMileage: data.show_mileage,
    showTechnicalDetails: data.show_technical_details,
    showEquipment: data.show_equipment,
    showMaintenance: data.show_maintenance,
    showHistory: data.show_history,
    showWarranty: data.show_warranty,
    showLocation: data.show_location,
    showContactInfo: data.show_contact_info
  };
};

export const useVehicles = (enabled: boolean = true) => {
  console.log('🔄 useVehicles hook called, enabled:', enabled);
  
  return useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - évite les refetch inutiles
    gcTime: 30 * 60 * 1000, // 30 minutes - garde en cache plus longtemps
    refetchOnWindowFocus: false, // Évite les refetch sur focus
  });
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteVehicle,
    onSuccess: () => {
      console.log('Delete mutation successful, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error) => {
      console.error('Delete mutation failed:', error);
    }
  });
};

export const useCreateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createVehicle,
    onSuccess: (data) => {
      console.log('Create mutation successful:', data);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error) => {
      console.error('Create mutation failed:', error);
    }
  });
};

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VehicleFormData }) => updateVehicle(id, data),
    onSuccess: (data) => {
      console.log('Update mutation successful:', data);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error) => {
      console.error('Update mutation failed:', error);
    }
  });
};

// Separate function to fetch vehicle images on-demand
const fetchVehicleImages = async (vehicleId: string): Promise<string[]> => {
  console.log('🖼️ Fetching images for vehicle:', vehicleId);
  
  const { data, error } = await supabase
    .from('vehicles')
    .select('images')
    .eq('id', vehicleId)
    .single();

  if (error) {
    console.error('❌ Error fetching vehicle images:', error);
    throw error;
  }
  
  console.log('✅ Vehicle images fetched successfully:', data?.images?.length || 0, 'images');
  return data?.images || [];
};

export const useVehicleImages = (vehicleId: string | null) => {
  return useQuery({
    queryKey: ['vehicle-images', vehicleId],
    queryFn: () => vehicleId ? fetchVehicleImages(vehicleId) : Promise.resolve([]),
    enabled: !!vehicleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
